/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import useScopedDashboardAccess from "@/auth/hooks/useScopedDashboardAccess";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";
import { projectStatus } from "@/constants/constants";
import { invalidateDashboardQueries } from "@/dashboard/dashboardQueryConfig";
import {
  PROJECT_QUERY_GC_TIME,
  PROJECT_QUERY_STALE_TIMES,
} from "@/dashboard/projectQueryConfig";
import { useProjectStore } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  CircleCheckBig,
  CircleX,
} from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RequestStatus from "../RequestStatus";

type HeaderDescriptifProps = {
  project: any;
  projectSummary?: any;
  hasDetailedProject?: boolean;
};

const isTruthyFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "true";

const isFalseFlag = (value: unknown): boolean =>
  value === false || value === 0 || value === "0" || value === "false";

const HeaderDescriptif = ({
  project,
  projectSummary,
  hasDetailedProject = true,
}: HeaderDescriptifProps) => {
  const { user } = useAuth();
  const { t } = useTranslation("dashboard", {
    keyPrefix: "project",
  });

  const { project: projectInStore, setProject } = useProjectStore();
  const queryClient = useQueryClient();
  const currentProject = useMemo(() => {
    if (projectInStore?.id && projectInStore.id === project?.id) {
      return projectInStore;
    }
    return project;
  }, [projectInStore, project]);
  const displayProject = projectSummary ?? currentProject;

  const hookPostData = usePostData();
  const { actsAsSubcontractorForScope, canManageScope } = useScopedDashboardAccess(
    currentProject?.program?.carrier?.id ?? (hasDetailedProject ? null : 0),
  );

  const [loadingValidate, setLoadingValidate] = useState(false);
  const [openValidate, setOpenValidate] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [openValidateUser, setOpenValidateUser] = useState(false);
  const [loadingValidateUser, setLoadingValidateUser] = useState(false);
  const [creatorValidationSuccess, setCreatorValidationSuccess] =
    useState(false);
  const [validateUserFromProjectModal, setValidateUserFromProjectModal] =
    useState(false);
  const isProjectCreatorCpIntervenant =
    currentProject?.creator?.roles?.includes("ROLE_CPINTERVENANT");
  const initialValue =
    isTruthyFlag(currentProject?.searchFinancingInternal)
      ? 1
      : isFalseFlag(currentProject?.searchFinancingInternal)
      ? -1
      : "";
  const [value, setValue] = useState(initialValue);
  const canValidateCandidate =
    value === 1 && isProjectCreatorCpIntervenant && !creatorValidationSuccess;
  const scoringData = useFetchDataV2<any>(
    hasDetailedProject &&
      currentProject?.status === projectStatus.Validated &&
      currentProject?.id
      ? `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/project/${currentProject.id}`
      : "",
    currentProject?.id ? `project-${currentProject.id}-scoring-summary` : "",
    PROJECT_QUERY_STALE_TIMES.scoringSummary,
    PROJECT_QUERY_GC_TIME,
  );
  const scoringSummary = scoringData.data?.scoringSummary ?? null;
  const scoringDecision = scoringData.data?.scoringDecision ?? null;
  const scoringSettings = scoringData.data?.scoringSettings ?? null;
  const isScoringEnabled = Boolean(scoringSettings?.enabled);
  const shouldShowNativeScoringHeader =
    currentProject?.status === projectStatus.Validated
    && isScoringEnabled
    && Boolean(scoringSummary)
    && (scoringDecision?.decisionMode ?? scoringSettings?.decisionMode) !== "manual_total";
  const scoringColor =
    scoringDecision?.decision === "auto_rejected"
      ? "text-red-600"
      : scoringDecision?.decision === "auto_accepted" || currentProject?.status === projectStatus.Validated
        ? "text-emerald-600"
        : "text-sky-600";

  useEffect(() => {
    if (openValidate) return;
    const nextValue =
      isTruthyFlag(currentProject?.searchFinancingInternal)
        ? 1
        : actsAsSubcontractorForScope
        ? 1
        : -1;
    setValue(nextValue);
  }, [currentProject?.id, currentProject?.searchFinancingInternal, actsAsSubcontractorForScope, openValidate]);

  const syncProjectStatusInCaches = (nextStatus: number) => {
    if (!currentProject?.id) return;

    const nextProject = {
      ...currentProject,
      status: nextStatus,
    };

    setProject(nextProject);

    [
      { tab: "descriptif", excludes: "indicator,reporting" },
      { tab: "synthetic", excludes: "indicator,reporting" },
      { tab: "suivi-perf", excludes: "reporting" },
      { tab: "reporting", excludes: "" },
    ].forEach((item) => {
      const queryString = item.excludes ? `?excludes=${item.excludes}` : "";
      const endpoint = `${import.meta.env.VITE_API_ENDPOINT}/project/${currentProject.id}${queryString}`;
      queryClient.setQueryData(
        [`project-${currentProject.id}-${item.tab}-v2`, endpoint],
        (current: any) => (current ? { ...current, status: nextStatus } : nextProject),
      );
    });

    queryClient.setQueriesData(
      { exact: false, queryKey: ["programsInfo"] },
      (current: unknown) => {
        if (!Array.isArray(current)) return current;

        let hasChanged = false;
        const nextPrograms = current.map((program: any) => {
          const projects = Array.isArray(program?.projects) ? program.projects : [];
          let programChanged = false;
          const nextProjects = projects.map((project: any) => {
            if (Number(project?.id ?? 0) !== Number(currentProject.id)) return project;
            programChanged = true;
            hasChanged = true;
            return { ...project, status: nextStatus };
          });

          return programChanged ? { ...program, projects: nextProjects } : program;
        });

        return hasChanged ? nextPrograms : current;
      },
    );
  };

  const refreshProjectStatusInBackground = (nextStatus: number) => {
    if (!currentProject?.id) return;

    const projectId = currentProject.id;
    window.setTimeout(() => {
      void Promise.allSettled([
        queryClient.refetchQueries({
          exact: false,
          queryKey: [`project-${projectId}-descriptif-v2`],
        }),
        queryClient.refetchQueries({
          exact: false,
          queryKey: ["programsInfo"],
        }),
        queryClient.refetchQueries({
          exact: false,
          queryKey: ["programsInfoTree"],
        }),
      ]).finally(() => {
        syncProjectStatusInCaches(nextStatus);
      });
      invalidateDashboardQueries(queryClient, "projectStatus");
    }, 1000);
  };

  const validateChanges = async (validate: boolean) => {
    setLoadingValidate(true);
    const previousStatus = Number(currentProject?.status);

    let statusTemp = 2;
    if (
      currentProject?.status === projectStatus.AwaitingValidationPP &&
      isFalseFlag(currentProject?.searchFinancingInternal) &&
      validate
    ) {
      statusTemp = projectStatus.AwaitingValidation;
      // setStatus(projectStatus.AwaitingValidation);
    } else if (
      currentProject?.status === projectStatus.AwaitingValidationPP &&
      isTruthyFlag(currentProject?.searchFinancingInternal) &&
      validate
    ) {
      statusTemp = projectStatus.Validated;
    } else if (
      currentProject?.status === projectStatus.AwaitingValidationPP &&
      !validate
    ) {
      statusTemp = projectStatus.Rejected;
    }

    syncProjectStatusInCaches(statusTemp);

    const res = await hookPostData.postData({
      url: `${import.meta.env.VITE_IP_API}/api/desired_funding/validate`,
      data: {
        id_project: currentProject?.id,
        status: statusTemp,
      },
    });
    if (res) {
      if (
        validate &&
        statusTemp === projectStatus.Validated &&
        isProjectCreatorCpIntervenant
      ) {
        setCreatorValidationSuccess(true);
      }
      toast({
        title: "Modification enregistrée !",
      });
      refreshProjectStatusInBackground(statusTemp);
      if (validate && statusTemp === projectStatus.Validated && currentProject?.id) {
        window.location.assign(`/dashboard/project/${currentProject.id}?tab=synthetic`);
        return;
      }
    } else if (Number.isFinite(previousStatus)) {
      syncProjectStatusInCaches(previousStatus);
    }
    // setStatus(statusTemp);
    setLoadingValidate(false);
    setOpenValidate(false);
    setOpenReject(false);
    setOpenValidateUser(false);
  };

  useEffect(() => {
    if (validateUserFromProjectModal && value !== 1 && openValidateUser) {
      setOpenValidateUser(false);
    }
  }, [value, openValidateUser, validateUserFromProjectModal]);

  const validateProjectCreator = async () => {
    if (!currentProject?.creator?.id) return;
    setLoadingValidateUser(true);
    const res = await hookPostData.postData({
      url: `${import.meta.env.VITE_IP_API}/api/users/call-project/validate`,
      data: {
        id_user: currentProject?.creator?.id,
      },
    });
    if (res) {
      toast({
        title: t("Candidat valide"),
      });
      setCreatorValidationSuccess(true);
    }
    setLoadingValidateUser(false);
    setOpenValidateUser(false);
    setValidateUserFromProjectModal(false);
  };

  return (
    <>
      <div>
        <ReactBreadcrumb
          items={[
            { href: "/dashboard/programs", name: t("Mes programmes") },
            {
              href: `/dashboard/program/${displayProject?.program?.id}`,
              name: displayProject?.program?.name || "",
            },
            { href: "", name: displayProject?.name || "" },
          ]}
        />

        <div className="relative flex justify-between gap-4 my-4">
          <div>
            <h1 className="mr-5 inline align-sub text-2xl font-medium">
              {t("Projet")} : {displayProject?.name}
            </h1>{" "}
            <Status status={displayProject?.status} />
            &nbsp;&nbsp;
            {currentProject?.soldStatus === 2 && currentProject?.notifSold !== true && (
              <Popover>
                <PopoverTrigger asChild>
                  <div className="mx-2 relative inline-block w-[15px] h-[15px]">
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        boxShadow: [
                          "0px 0px 0px rgba(0, 0, 0, 0)",
                          "0px 4px 12px rgba(128, 181, 51, 0.6)",
                          "0px 0px 0px rgba(0, 0, 0, 0)",
                        ],
                        color: ["#80B533", "#9DDC5D", "#80B533"], // vert clair vers foncé
                      }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.5,
                        ease: "easeInOut",
                      }}
                      className="absolute top-0 left-0 cursor-pointer rounded-full"
                    >
                      <CircleCheckBig size={22} />
                    </motion.div>
                  </div>
                </PopoverTrigger>
                <PopoverContent>
                  {t("Tous les financements ont été reçus")}
                </PopoverContent>
              </Popover>
            )}
            {currentProject?.soldStatus === 2 && currentProject?.notifSold === true && (
              <Popover>
                <PopoverTrigger asChild>
                  <div className="mx-2 relative inline-block w-[15px] h-[15px]">
                    <CircleCheckBig
                      className="absolute top-0 left-0 cursor-pointer rounded-full text-[#80B533]"
                      size={22}
                    />
                  </div>
                </PopoverTrigger>
                <PopoverContent>
                  {t("Tous les financements ont été reçus")}
                </PopoverContent>
              </Popover>
            )}
            <RequestStatus />
          </div>
          <div>
            {currentProject?.status === projectStatus.AwaitingValidationPP &&
              canManageScope && (
                <div className="flex flex-row gap-2">
                  <Button
                    onClick={() => {
                      setOpenValidate(true);
                    }}
                    className="ml-5 h-8 text-[12px]"
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t("Valider le projet")}
                  </Button>
                  <Button
                    onClick={() => {
                      setOpenReject(true);
                    }}
                    className="ml-5 h-8 text-[12px] bg-verdeored"
                  >
                    <CircleX className="mr-2 h-4 w-4" />
                    {t("Rejeter le projet")}
                  </Button>
                </div>
              )}
            {currentProject?.status === projectStatus.Validated &&
              isProjectCreatorCpIntervenant &&
              !creatorValidationSuccess &&
              canManageScope && (
                <div className="flex flex-row gap-2">
                  <Button
                    onClick={() => {
                      setValidateUserFromProjectModal(false);
                      setOpenValidateUser(true);
                    }}
                    className="ml-5 h-8 text-[12px]"
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t("Valider le candidat")}
                  </Button>
                </div>
              )}
          </div>
        </div>

        {shouldShowNativeScoringHeader && (
          scoringDecision?.decision === "auto_accepted" ? (
            <div className="mb-4 rounded-xl border border-[#dbe8d2] bg-[#f6fbf2] px-4 py-3 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2 leading-none">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#58714f]">Notation du projet</span>
                    <Badge className="h-6 rounded-full bg-emerald-100 px-2.5 text-[11px] font-medium text-emerald-700">
                      Validé automatiquement
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Décision basée sur le score calculé du formulaire.
                  </p>
                </div>
              </div>
            </div>
          ) : (
          <div className="mb-4 rounded-2xl border border-[#dbe8d2] bg-[#f6fbf2] px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2 leading-none">
                  <span className="text-sm font-semibold leading-none text-[#2f3f2d]">Notation du projet</span>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {scoringDecision?.decision === "auto_accepted"
                      ? "Projet validé automatiquement"
                      : "Projet validé"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {scoringSettings?.decisionMode === "auto_threshold"
                    ? "Décision basée uniquement sur les notes automatiques."
                    : scoringSettings?.decisionMode === "auto_total"
                      ? "Décision basée sur l'ensemble des notes automatiques et manuelles."
                      : "Décision finale prise après évaluation du score."}
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {scoringSettings?.rejectionPercentage !== null
                    && scoringSettings?.rejectionPercentage !== undefined && (
                    <span className="rounded-full bg-white px-2.5 py-1 shadow-sm">
                      Rejet auto jusqu&apos;à {Number(scoringSettings.rejectionPercentage).toFixed(0)}%
                    </span>
                  )}
                  {scoringSettings?.acceptancePercentage !== null
                    && scoringSettings?.acceptancePercentage !== undefined && (
                    <span className="rounded-full bg-white px-2.5 py-1 shadow-sm">
                      Validation auto à partir de {Number(scoringSettings.acceptancePercentage).toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
              <div className="rounded-2xl bg-white px-5 py-4 shadow-sm text-center min-w-[180px]">
                <p className={`text-4xl font-bold ${scoringColor}`}>
                  {Number(
                    scoringSummary.finalPercentage
                    ?? scoringSummary.currentPercentage
                    ?? scoringSummary.autoPercentage
                    ?? 0,
                  ).toFixed(0)}
                  %
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Score final
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {Number(scoringSummary.totalCurrentScore ?? 0).toFixed(2)} /{" "}
                  {Number(scoringSummary.totalIncludedMaxScore ?? 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          )
        )}

        {displayProject?.description && (
          <p
            className="mb-4 mt-2 text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: displayProject.description.replace(/\n/g, "<br />"),
            }}
          />
        )}
      </div>
      <Dialog open={openValidate} onOpenChange={setOpenValidate}>
        <DialogContent className="sm:max-w-[600px]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              validateChanges(true);
            }}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {t("Valider le projet")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "En cliquant sur valider, vous confirmez que les changements correspondent bien à vos besoins."
                )}
                {isFalseFlag(currentProject?.searchFinancingInternal) && (
                  <>
                    <br />
                    {t(
                      "Après votre validation, Verdeo effectuera une vérification et une validation finale."
                    )}
                  </>
                )}

              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="submit"
                disabled={loadingValidate}
              >
                {t("Valider")}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setOpenValidate(false);
                }}
                className="bg-slate-500"
              >
                {t("Annuler")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={openReject} onOpenChange={setOpenReject}>
        <DialogContent className="sm:max-w-[600px]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              validateChanges(false);
            }}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {t("Rejeter le projet")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "En cliquant sur Rejeter, vous confirmez votre décision et ce projet sera marqué comme rejeté."
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="submit" disabled={loadingValidate}>
                {t("Rejeter")}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setOpenReject(false);
                }}
                className="bg-slate-500"
              >
                {t("Annuler")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={openValidateUser} onOpenChange={setOpenValidateUser}>
        <DialogContent
          className="sm:max-w-[600px]"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {t("Valider le candidat")}
            </DialogTitle>
            <DialogDescription>
              {t("Souhaitez-vous valider le candidat ?")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              disabled={loadingValidateUser}
              onClick={validateProjectCreator}
            >
              {t("Valider le candidat")}
            </Button>
            <Button
              type="button"
              onClick={() => setOpenValidateUser(false)}
              className="bg-slate-500"
            >
              {t("Annuler")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Status: FC<{ status: number }> = ({ status }) => {
  let statusText = "";
  let bg = "background";
  let text = "foreground";

  const { t } = useTranslation("submit", {
    keyPrefix: "project",
  });

  const { project } = useProjectStore();

  const { user } = useAuth();

  switch (status) {
    case projectStatus.NotStarted:
      statusText = t("Non commencé");
      bg = "gray-200";
      text = "foreground";
      break;
    case projectStatus.InProgress:
      statusText = t("En cours");
      bg = "orangeProgress";
      text = "background";
      break;
    case projectStatus.AwaitingValidation:
      if (user?.roles?.includes("ROLE_SUBCONTRACTOR")) {
        statusText = t("En attente de validation");
      } else {
        statusText = t("En attente de validation");
      }
      bg = "verdeoblue";
      text = "background";
      break;
    case projectStatus.Validated:
      statusText = t("Validé");
      bg = "verdeogreen";
      text = "background";
      break;
    case projectStatus.Rejected:
      statusText = t("Refusé");
      bg = "verdeored";
      text = "background";
      break;
    case projectStatus.Closed:
      statusText = t("Cloturé");
      bg = "verdeoblue";
      text = "background";
      break;
    case projectStatus.AwaitingValidationPP:
      statusText = t("En attente de votre validation");
      bg = "verdeoblue";
      text = "background";
      break;
    case projectStatus.AwaitingSoumission:
      statusText = t("En attente de soumission");
      bg = "verdeogray";
      text = "background";
      break;
    default:
      break;
  }
  return (
    // eslint-disable-next-line tailwindcss/classnames-order, tailwindcss/no-custom-classname
    <Badge
      className={`inline-flex items-center bg-${bg} text-${text} whitespace-nowrap`}
    >
      {statusText}
    </Badge>
  );
};

export default HeaderDescriptif;
